import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Save, Trash2, Edit, Eye, Search, Calendar, CreditCard, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTEREST_TYPE_LABELS = {
  'fixed_2y': 'Fixă 2 ani',
  'fixed_3y': 'Fixă 3 ani',
  'fixed_5y': 'Fixă 5 ani',
  'fixed_10y': 'Fixă 10 ani',
  'fixed_full': 'Fixă toată perioada',
  'variable': 'Variabilă'
};

const PRODUCT_TYPE_LABELS = {
  'energy_class_a': 'Ipotecar Clasa A',
  'energy_class_bc': 'Ipotecar Clasa B/C',
  'refinance_a': 'Refinanțare A',
  'refinance_bc': 'Refinanțare B/C'
};

const SavedCalculations = ({ onLoadCalculation }) => {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  
  // Fetch calcule salvate
  const fetchCalculations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/calculator/saved`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalculations(response.data.calculations || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la încărcarea calculelor');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCalculations();
  }, []);
  
  // Șterge un calcul
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/calculator/saved/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalculations(prev => prev.filter(c => c.id !== id));
      setDeleteId(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Eroare la ștergerea calculului');
    }
  };
  
  // Filtrează după căutare
  const filteredCalculations = calculations.filter(calc =>
    calc.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Formatează data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculează zile rămase
  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 0;
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ro-RO').format(value) + ' RON';
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Se încarcă calculele...</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Save className="h-5 w-5" />
              Calcule Salvate
            </CardTitle>
            <CardDescription>
              Calculele se șterg automat după 6 luni
            </CardDescription>
          </div>
          <Badge variant="outline">
            {calculations.length} calcule
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {/* Căutare */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Caută după numele clientului..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}
        
        {/* Tabel */}
        {filteredCalculations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Save className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Nu există calcule salvate</p>
            <p className="text-sm">Calculele vor apărea aici după ce le salvați din calculator</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Tip Credit</TableHead>
                  <TableHead>Dobândă</TableHead>
                  <TableHead>Valoare</TableHead>
                  <TableHead>Perioadă</TableHead>
                  <TableHead>Expiră</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCalculations.map((calc) => {
                  const daysRemaining = getDaysRemaining(calc.expires_at);
                  
                  return (
                    <TableRow key={calc.id}>
                      <TableCell className="font-medium">
                        {calc.client_name}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="h-3 w-3" />
                          {formatDate(calc.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {PRODUCT_TYPE_LABELS[calc.product_type] || calc.product_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {INTEREST_TYPE_LABELS[calc.interest_type] || calc.interest_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-3 w-3 text-gray-400" />
                          {formatCurrency(calc.loan_amount)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {calc.loan_period_months / 12} ani
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={daysRemaining < 30 ? 'destructive' : 'outline'}
                          className="text-xs"
                        >
                          {daysRemaining} zile
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onLoadCalculation(calc)}
                            title="Încarcă calculul"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(calc.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Șterge calculul"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Dialog confirmare ștergere */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmare ștergere</AlertDialogTitle>
              <AlertDialogDescription>
                Sigur doriți să ștergeți acest calcul? Această acțiune nu poate fi anulată.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anulează</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteId)}
                className="bg-red-600 hover:bg-red-700"
              >
                Șterge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default SavedCalculations;
